import { useRef, useEffect } from 'react';
import { useWhatsAppLead } from '@/hooks/useWhatsAppLead';

interface DemoCard {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  url: string;
  icon: string;
  accentColor: string;
  borderColor: string;
  btnClass: string;
  iconBg: string;
}

const demoCards: DemoCard[] = [
  {
    id: 'demo1',
    eyebrow: 'Müşteri Gözüyle',
    title: 'Demo Örnek Platform 1',
    description: 'Gerçek bir müşterinin gözünden platformun nasıl göründüğünü keşfedin. Hizmet sayfaları, iletişim formu ve mobil deneyimi canlı olarak inceleyin.',
    url: 'https://saloons.smarthizmet.com/',
    icon: 'ri-user-smile-line',
    accentColor: 'text-gold',
    borderColor: 'border-gold/25',
    btnClass: 'btn-outline-gold',
    iconBg: 'bg-gold/10',
  },
  {
    id: 'demo2',
    eyebrow: 'Müşteri Gözüyle',
    title: 'Demo Örnek Platform 2',
    description: 'Farklı bir sektör ve tasarım anlayışıyla hazırlanmış ikinci demo platformumuzu ziyaret edin. Dönüşüm odaklı yapıyı bizzat deneyimleyin.',
    url: 'https://demoshp.smarthizmet.com',
    icon: 'ri-store-2-line',
    accentColor: 'text-neon',
    borderColor: 'border-neon/25',
    btnClass: 'btn-outline-neon',
    iconBg: 'bg-neon/10',
  },
  {
    id: 'demo3',
    eyebrow: 'Yönetici Gözüyle',
    title: 'Açıklamalı Demo Örnek Platformu',
    description: 'Platformun tüm özelliklerini yönetici perspektifinden, detaylı açıklamalarla inceleyin. Her modülün nasıl çalıştığını adım adım görün.',
    url: 'https://demodetayshp.smarthizmet.com',
    icon: 'ri-settings-4-line',
    accentColor: 'text-purple-400',
    borderColor: 'border-purple-400/25',
    btnClass: 'btn-outline-purple',
    iconBg: 'bg-purple-400/10',
  },
];

interface AdminDemoCard {
  eyebrow: string;
  title: string;
  description: string;
  url: string;
  icon: string;
  stats: { label: string; value: string }[];
}

export default function DemoPackagesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { openModal } = useWhatsAppLead();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.demo-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="demo-packages"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: '#0a0a12' }}
    >
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(ellipse at 50% 0%, #f0b429, transparent 55%)' }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8" ref={ref}>
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/20 px-4 py-2 rounded-full mb-5">
            <i className="ri-eye-line text-gold text-sm" />
            <span className="text-gold text-sm font-semibold">Canlı Demolar</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            Demo Site Platformlarını{' '}
            <span className="text-gold-gradient">İncele</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Satın almadan önce platformu canlı olarak görün. Hem müşteri hem yönetici gözünden deneyimleyin.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {demoCards.map((card) => (
            <div
              key={card.id}
              className={`demo-card relative rounded-3xl p-7 border glass flex flex-col ${card.borderColor} transition-all duration-300`}
              style={{
                opacity: 0,
                transform: 'translateY(40px)',
                transition: 'all 0.5s ease',
              }}
            >
              {/* Icon */}
              <div className={`w-12 h-12 flex items-center justify-center rounded-2xl ${card.iconBg} border ${card.borderColor} mb-5`}>
                <i className={`${card.icon} ${card.accentColor} text-xl`} />
              </div>

              {/* Eyebrow */}
              <p className={`text-xs font-black uppercase tracking-widest mb-2 ${card.accentColor} opacity-80`}>
                {card.eyebrow}
              </p>

              {/* Title */}
              <h3 className="text-xl font-extrabold text-white mb-3 leading-snug">
                {card.title}
              </h3>

              {/* Description */}
              <p className="text-gray-400 text-sm leading-relaxed mb-8 flex-1">
                {card.description}
              </p>

              {/* CTA */}
              <a
                href={card.url}
                target="_blank"
                rel="nofollow noreferrer"
                className={`w-full py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${card.btnClass}`}
              >
                <i className="ri-external-link-line text-base" />
                İncele
              </a>
            </div>
          ))}
        </div>

        {/* Admin Analytics Demo — full-width highlight card */}
        <div
          className="demo-card mt-8 rounded-3xl border border-emerald-500/25 glass overflow-hidden"
          style={{ opacity: 0, transform: 'translateY(40px)', transition: 'all 0.5s ease 0.45s' }}
        >
          <div className="flex flex-col lg:flex-row items-stretch">
            {/* Left: content */}
            <div className="flex-1 p-8 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-emerald-500/10 border border-emerald-500/25">
                  <i className="ri-bar-chart-2-line text-emerald-400 text-xl" />
                </div>
                <p className="text-xs font-black uppercase tracking-widest text-emerald-400 opacity-80">
                  Yönetici Paneli
                </p>
              </div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-snug">
                Platformunuzun Admin Analiz Demosunu İnceleyin
              </h3>
              <p className="text-gray-400 text-base leading-relaxed mb-6 max-w-xl">
                Gerçek zamanlı randevu istatistikleri, gelir grafikleri, müşteri davranış analizleri ve personel performans raporlarını içeren yönetici panelini canlı olarak keşfedin. İşletmenizin tüm verilerini tek ekranda nasıl yöneteceğinizi görün.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-line-chart-line', label: 'Gelir Grafikleri' },
                  { icon: 'ri-calendar-check-line', label: 'Randevu Analitiği' },
                  { icon: 'ri-team-line', label: 'Personel Raporları' },
                  { icon: 'ri-pie-chart-2-line', label: 'Müşteri Segmentasyonu' },
                ].map((tag) => (
                  <span
                    key={tag.label}
                    className="inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold px-3 py-1.5 rounded-full"
                  >
                    <i className={`${tag.icon} text-sm`} />
                    {tag.label}
                  </span>
                ))}
              </div>
              <a
                href="https://demoadminshp.smarthizmet.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="btn-gold inline-flex items-center gap-2 self-start px-7 py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line text-base" />
                Demo Admin Panelini İncele
              </a>
            </div>

            {/* Right: decorative stats panel */}
            <div className="lg:w-72 xl:w-80 bg-emerald-500/5 border-t lg:border-t-0 lg:border-l border-emerald-500/15 p-8 flex flex-col justify-center gap-5">
              {[
                { label: 'Aylık Randevu', value: '1.240', icon: 'ri-calendar-2-line', change: '+18%' },
                { label: 'Toplam Gelir', value: '₺84.500', icon: 'ri-money-dollar-circle-line', change: '+24%' },
                { label: 'Aktif Müşteri', value: '386', icon: 'ri-user-heart-line', change: '+11%' },
                { label: 'Personel Verimi', value: '%92', icon: 'ri-award-line', change: '+5%' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 shrink-0">
                    <i className={`${stat.icon} text-emerald-400 text-base`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-xs mb-0.5">{stat.label}</p>
                    <p className="text-white font-extrabold text-base leading-none">{stat.value}</p>
                  </div>
                  <span className="text-emerald-400 text-xs font-bold bg-emerald-500/10 px-2 py-0.5 rounded-full shrink-0">
                    {stat.change}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div className="text-center mt-10">
          <p className="text-gray-500 text-sm">
            Demo platformları gerçek müşteri verisi içermez.{' '}
            <span className="text-gray-400">Kendi işletmenize özel demo için</span>{' '}
            <button
              onClick={() => openModal('Demo Bölümü — Özel Demo Talebi')}
              className="text-gold font-semibold hover:underline cursor-pointer"
            >
              WhatsApp&apos;tan ulaşın
            </button>
            .
          </p>
        </div>
      </div>
    </section>
  );
}
